// <pos-xiaohong-chat/> — 小掌櫃 AI 店長對話（內部代號「小紘」對話腦）
// see docs/AI_STORE_MANAGER_DESIGN_v0.1.md + architecture.md#L410
// see src/lib/branding.js（品牌命名分層）
// Phase A Day 1：單輪問答接後端 / Phase B Day 1-2：tool use + 對話歷史

import { askXiaohong } from '../lib/api.js';
import { BRAND_NAME, BRAND_TAGLINE } from '../lib/branding.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

class PosXiaohongChat extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.messages = [];
    this.sessionId = null;
    this.sending = false;
  }

  connectedCallback() {
    this.render();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: flex; flex-direction: column; height: calc(100vh - 64px); padding: var(--s-5); max-width: 900px; margin: 0 auto; }
        h2 { font-family: var(--font-display); color: var(--brand-primary); margin: 0 0 var(--s-4); }
        .msgs { flex: 1; overflow-y: auto; padding: var(--s-4); background: var(--bg-elevated); border-radius: var(--r-lg); }
        .msg { margin-bottom: var(--s-3); display: flex; }
        .msg[data-role="user"] { justify-content: flex-end; }
        .msg[data-role="assistant"] { justify-content: flex-start; }
        .bubble { display: inline-block; padding: var(--s-3) var(--s-4); border-radius: var(--r-lg); max-width: 75%; white-space: pre-wrap; word-break: break-word; }
        .msg[data-role="user"] .bubble { background: var(--brand-primary); color: white; }
        .msg[data-role="assistant"] .bubble { background: var(--bg-surface); color: var(--fg); border: 1px solid var(--border); }
        .msg[data-role="error"] .bubble { background: rgba(220,38,38,.1); color: var(--danger); border: 1px solid var(--danger); }
        .meta { font-size: .7rem; color: var(--fg-muted); margin-top: 4px; }
        form { display: flex; gap: var(--s-3); margin-top: var(--s-4); }
        input {
          flex: 1; padding: var(--s-3) var(--s-4); background: var(--bg); color: var(--fg);
          border: 1px solid var(--border); border-radius: var(--r-md); font: inherit;
        }
        input:focus { outline: none; border-color: var(--brand-primary); }
        button {
          padding: var(--s-3) var(--s-5); background: var(--brand-primary); color: white;
          border: 0; border-radius: var(--r-md); cursor: pointer; font: inherit; font-weight: 600;
        }
        button:disabled { opacity: .5; cursor: not-allowed; }
        .empty { color: var(--fg-muted); text-align: center; padding: var(--s-10); line-height: 1.8; }
        .tips { font-size: .85rem; color: var(--fg-muted); margin-top: var(--s-2); }
      </style>

      <h2>💬 ${escapeHtml(BRAND_NAME)} · ${escapeHtml(BRAND_TAGLINE)}</h2>

      <div class="msgs" id="msgs">
        ${this.messages.length === 0 ? `
          <div class="empty">
            ${escapeHtml(BRAND_NAME)}準備好了，問什麼都可以：<br/><br/>
            • 「今天營收多少？」<br/>
            • 「這週哪道菜賣最好？」<br/>
            • 「雞腿快過期了怎麼推？」<br/>
            • 「幫我寫張 LINE 推播」
          </div>
        ` : this.messages.map((m) => `
          <div class="msg" data-role="${m.role}">
            <div>
              <div class="bubble">${escapeHtml(m.content)}</div>
              ${m.meta ? `<div class="meta">${escapeHtml(m.meta)}</div>` : ''}
            </div>
          </div>
        `).join('')}
      </div>

      <form id="f">
        <input type="text" id="q" placeholder="問${escapeHtml(BRAND_NAME)}..." maxlength="500" required ${this.sending ? 'disabled' : ''} />
        <button type="submit" ${this.sending ? 'disabled' : ''}>${this.sending ? '思考中…' : '送出'}</button>
      </form>
      <div class="tips">${escapeHtml(BRAND_NAME)} · 鍵盤可按麥克風圖案用 OS 語音輸入（不收音費）</div>
    `;

    const form = this.shadowRoot.querySelector('#f');
    form.addEventListener('submit', (e) => this.onSubmit(e));
    // auto-scroll to bottom
    const msgs = this.shadowRoot.querySelector('#msgs');
    msgs.scrollTop = msgs.scrollHeight;
    // 送出後 focus 回 input
    if (!this.sending) this.shadowRoot.querySelector('#q')?.focus();
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.sending) return;
    const input = this.shadowRoot.querySelector('#q');
    const q = input.value.trim();
    if (!q) return;

    this.messages.push({ role: 'user', content: q });
    this.sending = true;
    input.value = '';
    this.render();

    try {
      const res = await askXiaohong({ question: q, session_id: this.sessionId });
      this.sessionId = res.session_id;

      // Phase B: 顯示用了哪些 tool（讓店長看到透明度）
      let toolMeta = '';
      if (Array.isArray(res.tool_calls) && res.tool_calls.length > 0) {
        const TOOL_ZH = {
          query_sales: '查銷售',
          query_inventory: '查庫存',
          query_expiring: '查效期',
        };
        const labels = res.tool_calls.map((tc) => {
          const zh = TOOL_ZH[tc.name] || tc.name;
          const mark = tc.success === false ? '✗' : '✓';
          return `${mark}${zh}`;
        });
        toolMeta = ` · 🔧 ${labels.join(' ')}`;
      }

      const KEY_SOURCE_LABEL = {
        byok_primary: '主要 key',
        byok_fallback: '備援 key',
        platform: '平台 key',
        byok: '你的 key',  // backward compat
      };
      const ksLabel = KEY_SOURCE_LABEL[res.key_source] || res.key_source || '?';

      this.messages.push({
        role: 'assistant',
        content: res.answer,
        meta: `${res.model} · ${res.latency_ms}ms · ${ksLabel}${toolMeta}`,
      });
    } catch (err) {
      this.messages.push({
        role: 'error',
        content: `✗ ${err.message_zh || err.message || `${BRAND_NAME}連線失敗`}`,
      });
    } finally {
      this.sending = false;
      this.render();
    }
  }
}

customElements.define('pos-xiaohong-chat', PosXiaohongChat);
